import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import ReactDOM from "react-dom";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { Checkbox } from "@/components/Checkbox/Checkbox";

import { InputField } from "@/components/InputField/InputField";

import {
	useSendTestSignatureMutation,
	useUpdateEmailMutation,
} from "@/pages/Private/redux/email/email.api";

import { Modal } from "@/components/Modals/Modal";

import { Icon } from "@/components";

import { useSenderDeletion } from "../hooks/useSenderDeletion";
import { PendingSchedulesDialog } from "./PendingSchedulesDialog";

import {
	Email,
	EmailSchema,
	SendTestSignatureFormValues,
	SendTestSignatureRequest,
	SendTestSignatureRequestSchema,
} from "../schema/email";
import { SignatureEditor } from "./SignatureEditor";

interface EmailPanelProps {
	email: Email;
	handleClose: () => void;
	allowDelete?: boolean;
}

export const EmailPanel: FunctionComponent<EmailPanelProps> = ({
	email,
	handleClose,
	allowDelete = false,
}) => {
	const [updateEmail, { isLoading: isUpdateLoading }] = useUpdateEmailMutation();
	const [sendTestSignature, { isLoading: isTestLoading }] = useSendTestSignatureMutation();

	const {
		initiateDeletion,
		confirmDeletion,
		cancelDeletion,
		showPendingDialog,
		totalCount,
		campaigns,
		isCheckingPending,
		isDeleting,
	} = useSenderDeletion({
		senderType: "email",
		emailId: email.id,
		onSuccess: () => handleClose(),
	});

	const [saveForAllOtherEmailsOfThisUser, setSaveForAllOtherEmailsOfThisUser] =
		useState<boolean>(false);

	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showTestModal, setShowTestModal] = useState<boolean>(false);
	const [testSuccess, setTestSuccess] = useState<string | null>(null);
	const [testError, setTestError] = useState<string | null>(null);

	const {
		setValue,
		watch,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<Email>({
		resolver: zodResolver(EmailSchema),
	});

	if (Object.values(errors).length) {
		console.log(errors);
	}

	const formValues = watch();

	useEffect(() => {
		if (email) {
			reset({
				...email,
			});
		}
	}, [email]);

	const {
		setValue: setTestValue,
		watch: watchTest,
		handleSubmit: handleTestSubmit,
		reset: resetTest,
		formState: { errors: testFormErrors },
	} = useForm<SendTestSignatureFormValues, unknown, SendTestSignatureRequest>({
		resolver: zodResolver(SendTestSignatureRequestSchema),
		defaultValues: {
			emailId: email.id,
			recipientEmail: "",
			testMessage: "",
		},
	});

	const testFormValues = watchTest();

	const onSubmit = async (values: Email) => {
		try {
			await updateEmail({
				...values,
				saveForAllOtherEmailsOfThisUser,
			}).unwrap();

			reset();
			handleClose();
		} catch (err) {
			console.error(err);
		}
	};

	const handleDelete = async () => {
		setShowDeleteModal(true);
	};

	const handleConfirmDelete = async () => {
		await initiateDeletion();
		setShowDeleteModal(false);
	};

	const handleSendTest = async (values: SendTestSignatureRequest) => {
		setTestSuccess(null);
		setTestError(null);

		try {
			const result = await sendTestSignature({
				emailId: email.id,
				testMessage: values.testMessage,
				...(values.recipientEmail ? { recipientEmail: values.recipientEmail } : {}),
			}).unwrap();

			setTestSuccess(result.message);
		} catch (err) {
			setTestError(
				(err as { data?: { message?: string } })?.data?.message ?? "Failed to send test email"
			);
		}
	};

	return ReactDOM.createPortal(
		<div className="flex flex-col rounded-xl border border-gray-200 bg-brand-white p-4">
			{email && (
				<div className="fixed right-0 top-0 z-[5000] h-full w-2/3 bg-brand-white border-l border-gray-200">
					<div className="flex flex-col p-4 h-full">
						<div className="text-text text-smd">Edit E-Mail settings for {email.email}</div>
						<Icon
							className="absolute top-4 right-4 cursor-pointer w-5 h-5"
							icon={faXmark}
							onClick={handleClose}
						/>
						<div className="flex flex-col gap-2 h-full">
							<div className="text-text text-sm mb-8">
								You can edit the settings for your E-Mail address.
							</div>
							<div className="flex flex-col gap-2">
								<div className="text-text text-sm">Sender information</div>
							</div>
							<div className="flex flex-col gap-4 h-full">
								<div className="flex flex-row gap-4">
									<InputField
										handleChange={(e) => setValue("senderFirstName", e.target.value)}
										label="Sender First Name"
										name="senderFirstName"
										type="text"
										value={formValues.senderFirstName ?? ""}
									/>
									<InputField
										handleChange={(e) => setValue("senderLastName", e.target.value)}
										label="Sender Last Name"
										name="senderLastName"
										type="text"
										value={formValues.senderLastName ?? ""}
									/>
								</div>
								<div className="flex flex-col gap-1">
									<InputField
										error={errors.limitPerDay?.message}
										handleChange={(e) => setValue("limitPerDay", Number(e.target.value))}
										label="Limit per day"
										max={500}
										min={1}
										name="limitPerDay"
										showError
										type="number"
										value={formValues.limitPerDay ?? 0}
									/>
									{errors.limitPerDay?.message && (
										<p className="text-xs text-status-error">{errors.limitPerDay.message}</p>
									)}
								</div>

								<div className="flex flex-col gap-2">
									<div className="flex flex-row items-center justify-between">
										<div className="text-text text-sm">E-Mail Signature</div>
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											title={"Send Test Email"}
											onClick={() => {
												resetTest({
													emailId: email.id,
													recipientEmail: "",
													testMessage: "",
												});
												setTestSuccess(null);
												setTestError(null);
												setShowTestModal(true);
											}}
										/>
									</div>
									<div className="w-full min-h-[120px] bg-white rounded-xl border border-gray-200">
										<SignatureEditor
											handleChange={(value: string) => setValue("signature", value)}
											signature={formValues.signature ?? ""}
										/>
									</div>
								</div>
								<div className="flex flex-row gap-4 items-center">
									<Checkbox
										isChecked={saveForAllOtherEmailsOfThisUser}
										onChange={() =>
											setSaveForAllOtherEmailsOfThisUser(!saveForAllOtherEmailsOfThisUser)
										}
									>
										<div className="text-text text-sm grow">
											Save sending data including signature for all other emails of this user
										</div>
									</Checkbox>
								</div>
								<div className="flex flex-col h-full justify-between">
									<div className="flex flex-row gap-4">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											isLoading={isUpdateLoading}
											title={"Close"}
											onClick={handleClose}
										/>
										<Button
											color={ButtonColor.ACTION}
											isLoading={isUpdateLoading}
											title={"Save"}
											onClick={handleSubmit(onSubmit)}
										/>
									</div>
									{allowDelete && (
										<div className="flex flex-row gap-4 justify-end">
											<Button
												color={ButtonColor.DANGER}
												isLoading={isDeleting || isCheckingPending}
												title={"Delete"}
												onClick={handleDelete}
											/>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
			{showDeleteModal && (
				<div className="z-[15001]">
					<Modal
						handleClose={() => setShowDeleteModal(false)}
						handleSave={handleConfirmDelete}
						isLoading={isDeleting || isCheckingPending}
						isOpened={showDeleteModal}
						submitButtonColor={ButtonColor.DANGER}
						submitButtonText={"Delete"}
						title={"Delete E-Mail"}
						zIndex={15001}
					>
						<div className="flex flex-col gap-4">
							<div className="text-text text-sm">Are you sure you want to delete this E-Mail?</div>
						</div>
					</Modal>
				</div>
			)}
			{showTestModal && (
				<div className="z-[15001]">
					<Modal
						handleClose={() => setShowTestModal(false)}
						handleSave={handleTestSubmit(handleSendTest)}
						isLoading={isTestLoading}
						isOpened={showTestModal}
						submitButtonText={"Send Test"}
						title={"Send Test Email"}
						zIndex={15001}
					>
						<div className="flex flex-col gap-4">
							<div className="text-text-meta text-sm">
								Send a test email to preview how your signature looks in a real inbox.
							</div>
							<InputField
								handleChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
									setTestValue("recipientEmail", e.target.value)
								}
								isOptional={true}
								label="Recipient Email (optional)"
								name="recipientEmail"
								placeholder="Leave empty to send to your own email"
								type="email"
								value={testFormValues.recipientEmail ?? ""}
							/>
							<InputField
								handleChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
									setTestValue("testMessage", e.target.value)
								}
								error={!!testFormErrors.testMessage?.message}
								label="Test Message"
								multiline
								name="testMessage"
								placeholder="Enter a test message body..."
								rows={3}
								value={testFormValues.testMessage ?? ""}
							/>
							{testSuccess && <div className="text-status-success text-sm">{testSuccess}</div>}
							{testError && <div className="text-status-error text-sm">{testError}</div>}
						</div>
					</Modal>
				</div>
			)}
			<PendingSchedulesDialog
				campaigns={campaigns}
				isLoading={isDeleting}
				isOpen={showPendingDialog}
				onClose={cancelDeletion}
				onConfirm={confirmDeletion}
				senderType="email"
				totalCount={totalCount}
				zIndex={15001}
			/>
		</div>,
		document.body
	);
};
